import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form } from "react-bootstrap";
import { useUpdateProfileBusinessPartnerMutation } from "../../../app/services/chatbot-rtk.services";
import { IProfileFormData } from "./partnerProfile";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

type Props = {};

export interface IPayoutInfo {
  payout: Payout;
  default: string;
}

export interface Payout {
  methods: Methods;
}

export interface Methods {
  paypal: Paypal;
  crypto: Crypto;
  bank: Bank;
}

export interface Paypal {
  address: string;
}

export interface Crypto {
  currency: string;
  network: string;
  address: string;
}

export interface Bank {
  fullname: string;
  iban: string;
  bankname: string;
  bicswiftcode: string;
}

type FormData = {
  PayoutMethod: "bank" | "crypto" | "paypal";
  paypalAddress: string;
  fullname: string;
  iban: string;
  bankname: string;
  bicswiftcode: string;
  walletAddress: string;
};

const validationSchema = Yup.object().shape({
  PayoutMethod: Yup.string()
    .oneOf(["paypal", "bank", "crypto"], "Invalid Payout method")
    .required("Payout method is required"),

  paypalAddress: Yup.string()
    .nullable()
    .when("PayoutMethod", (PayoutMethod: any, schema) =>
      PayoutMethod[0] === "paypal"
        ? schema.required("PayPal address is required")
        : schema
    ),

  fullname: Yup.string()
    .nullable()
    .when("PayoutMethod", (PayoutMethod: any, schema) =>
      PayoutMethod[0] === "bank"
        ? schema.required("Full name is required")
        : schema
    ),

  iban: Yup.string()
    .nullable()
    .when("PayoutMethod", (PayoutMethod: any, schema) =>
      PayoutMethod[0] === "bank" ? schema.required("IBAN is required") : schema
    ),

  bankname: Yup.string()
    .nullable()
    .when("PayoutMethod", (PayoutMethod: any, schema) =>
      PayoutMethod[0] === "bank"
        ? schema.required("Bank name is required")
        : schema
    ),

  bicswiftcode: Yup.string()
    .nullable()
    .when("PayoutMethod", (PayoutMethod: any, schema) =>
      PayoutMethod[0] === "bank"
        ? schema.required("BIC/Swift code is required")
        : schema
    ),

  walletAddress: Yup.string()
    .nullable()
    .when("PayoutMethod", (PayoutMethod: any, schema) =>
      PayoutMethod[0] === "crypto"
        ? schema.required("Wallet address is required")
        : schema
    )
});

function PartnerPayout({ profile }: { profile: IProfileFormData }) {
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    trigger,
    clearErrors,
    setValue,
    formState: { errors, isValid, isValidating }
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema)
  });
  const { t } = useTranslation();

  useEffect(() => {
    if (profile.payoutInfo) {
      setValue("PayoutMethod", profile.payoutInfo.default as any);
      setValue(
        "walletAddress",
        profile.payoutInfo.payout.methods.crypto.address
      );
      setValue(
        "paypalAddress",
        profile.payoutInfo.payout.methods.paypal.address
      );
      setValue("bankname", profile.payoutInfo.payout.methods.bank.bankname);
      setValue("fullname", profile.payoutInfo.payout.methods.bank.fullname);
      setValue("iban", profile.payoutInfo.payout.methods.bank.iban);
      setValue(
        "bicswiftcode",
        profile.payoutInfo.payout.methods.bank.bicswiftcode
      );
    }
  }, [profile]);

  const PayoutMethod = watch("PayoutMethod");

  const [updateProfileBusinessPartner, updateProfileBusinessPartnerStatus] =
    useUpdateProfileBusinessPartnerMutation();

  const onSubmit = (data: FormData) => {
    let payoutInfo: IPayoutInfo | undefined;

    if (profile?.payoutInfo) {
      payoutInfo = JSON.parse(JSON.stringify(profile?.payoutInfo));
    }

    if (!payoutInfo) {
      payoutInfo = {
        default: data.PayoutMethod,
        payout: {
          methods: {
            crypto: {
              currency: "USDT",
              network: "TRC20",
              address: data.walletAddress
            },
            bank: {
              bankname: "",
              bicswiftcode: "",
              fullname: "",
              iban: ""
            },
            paypal: {
              address: ""
            }
          }
        }
      };
    }

    switch (data.PayoutMethod) {
      case "crypto":
        payoutInfo.payout.methods.crypto.address = data.walletAddress;
        break;
      case "paypal":
        payoutInfo.payout.methods.paypal.address = data.paypalAddress;
        break;
      case "bank":
        payoutInfo.payout.methods.bank.bankname = data.bankname;
        payoutInfo.payout.methods.bank.fullname = data.fullname;
        payoutInfo.payout.methods.bank.bicswiftcode = data.bicswiftcode;
        payoutInfo.payout.methods.bank.iban = data.iban;
        break;
      default:
        break;
    }

    const profileInfo = { ...profile };

    profileInfo.payoutInfo = payoutInfo;

    updateProfileBusinessPartner(profileInfo)
      .unwrap()
      .then((res) => {
        toast.success(t("BusinessPartner/Payout/successEdit"));
      })
      .catch((err) => {
        toast.error(err?.data?.message || err?.message);
      });
  };

  const onSetAsDefault = () => {
    const data = getValues();

    if (!data.PayoutMethod || isValid == false) {
      trigger();
      return;
    } else {
      clearErrors();
    }

    let payoutInfo: IPayoutInfo | undefined;

    if (profile?.payoutInfo) {
      payoutInfo = JSON.parse(JSON.stringify(profile?.payoutInfo));
    }

    if (!payoutInfo) {
      payoutInfo = {
        default: data.PayoutMethod,
        payout: {
          methods: {
            crypto: {
              currency: "USDT",
              network: "TRC20",
              address: data.walletAddress
            },
            bank: {
              bankname: "",
              bicswiftcode: "",
              fullname: "",
              iban: ""
            },
            paypal: {
              address: ""
            }
          }
        }
      };
    }

    switch (data.PayoutMethod) {
      case "crypto":
        payoutInfo.payout.methods.crypto.address = data.walletAddress;
        break;
      case "paypal":
        payoutInfo.payout.methods.paypal.address = data.paypalAddress;
        break;
      case "bank":
        payoutInfo.payout.methods.bank.bankname = data.bankname;
        payoutInfo.payout.methods.bank.fullname = data.fullname;
        payoutInfo.payout.methods.bank.bicswiftcode = data.bicswiftcode;
        payoutInfo.payout.methods.bank.iban = data.iban;
        break;
      default:
        break;
    }

    const profileInfo = { ...profile };

    payoutInfo.default = data.PayoutMethod;

    profileInfo.payoutInfo = payoutInfo;

    updateProfileBusinessPartner(profileInfo)
      .unwrap()
      .then((res) => {
        toast.success(t("BusinessPartner/Payout/successEdit"));
      })
      .catch((err) => {
        toast.error(err?.data?.message || err?.message);
      });
  };

  return (
    <div className="tw-w-full tw-px-6 tw-py-12 tw-bg-white tw-rounded-2xl tw-shadow tw-flex tw-flex-col tw-gap-6">
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="lg:tw-mx-auto tw-mx-0 tw-flex tw-flex-col tw-gap-6"
      >
        <div className="tw-text-center tw-text-gray-800 tw-text-2xl tw-font-semibold tw-font-['Lexend']">
          {t("BusinessPartner/Payout/confirmMethod")}
        </div>

        {/* Payment Method Selection */}
        <Form.Group className="tw-w-full tw-relative">
          <Form.Label className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend']">
            {t("BusinessPartner/Payout/howReceive")}
          </Form.Label>
          <div className="tw-relative">
            <Form.Select
              {...register("PayoutMethod")}
              isInvalid={!!errors.PayoutMethod}
              className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md"
            >
              <option value="">
                {t("BusinessPartner/Payout/selectMethod")}
              </option>
              <option value="paypal">
                {t("BusinessPartner/Payout/paypal")}
              </option>
              <option value="bank">{t("BusinessPartner/Payout/credit")}</option>
              <option value="crypto">
                {t("BusinessPartner/Payout/crypto")}
              </option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.PayoutMethod?.message}
            </Form.Control.Feedback>

            {/* Default Badge */}
            {watch("PayoutMethod") === profile.payoutInfo?.default && (
              <div className="tw-absolute tw-top-1/2 tw-right-4 -tw-translate-y-1/2 tw-bg-emerald-100 tw-rounded-md tw-px-2 tw-py-1">
                <span className="tw-text-emerald-800 tw-text-xs">
                  {t("BusinessPartner/Payout/default")}
                </span>
              </div>
            )}
          </div>
        </Form.Group>

        {/* Conditional Form Fields */}
        {PayoutMethod === "paypal" && (
          <Form.Group className="tw-w-full">
            <Form.Label className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend']">
              {t("BusinessPartner/Payout/paypalAddress")}
            </Form.Label>
            <Form.Control
              type="email"
              placeholder={t("BusinessPartner/Payout/paypalValidation")}
              {...register("paypalAddress")}
              isInvalid={!!errors.paypalAddress}
              className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md"
            />
            <Form.Control.Feedback type="invalid">
              {errors.paypalAddress?.message}
            </Form.Control.Feedback>
          </Form.Group>
        )}

        {PayoutMethod === "bank" && (
          <>
            {["fullname", "iban", "bankname", "bicswiftcode"].map(
              (field: any, index) => (
                <Form.Group key={index} className="tw-w-full">
                  <Form.Label className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend']">
                    {t(`BusinessPartner/Payout/${field}`)}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    {...register(field as any)}
                    isInvalid={!!errors[field]}
                    className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[field]?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              )
            )}
          </>
        )}
        {PayoutMethod === "crypto" && (
          <>
            <Form.Group className="tw-w-full">
              <Form.Label className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend']">
                {t("BusinessPartner/Payout/currency")}
              </Form.Label>
              <Form.Control
                disabled
                type="text"
                placeholder="USDT"
                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md"
              />
            </Form.Group>
            <Form.Group className="tw-w-full">
              <Form.Label className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend']">
                {t("BusinessPartner/Payout/network")}
              </Form.Label>
              <Form.Control
                disabled
                type="text"
                placeholder="TRC20"
                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md"
              />
            </Form.Group>
            <Form.Group className="tw-w-full">
              <Form.Label className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend']">
                {t("BusinessPartner/Payout/walletAddress")}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={t("BusinessPartner/Payout/walletAddress")}
                {...register("walletAddress")}
                isInvalid={!!errors.walletAddress}
                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md"
              />
              <Form.Control.Feedback type="invalid">
                {errors.walletAddress?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </>
        )}

        {/* Buttons */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mt-4">
          <button
            type="submit"
            className="tw-border-none tw-px-[21px] tw-py-[7px] tw-bg-primary tw-rounded-[99px] tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend']"
          >
            {t("BusinessPartner/Payout/confirm")}
          </button>
          <button
            type="button"
            onClick={onSetAsDefault}
            className="tw-border-none tw-px-[21px] tw-py-[7px] tw-bg-green-500 tw-rounded-[99px] tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend']"
          >
            {t("BusinessPartner/Payout/setDefault")}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default PartnerPayout;
